import {
  Column,
  ColumnDef,
  ColumnFiltersState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  InitialTableState,
  RowSelectionState,
  SortingState,
  useReactTable,
} from '@tanstack/react-table';
import filter from 'lodash/filter';
import get from 'lodash/get';
import {
  CSSProperties,
  PropsWithChildren,
  ReactNode,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useDebounce } from 'use-debounce';

import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useGetTestId } from '@/hooks';
import { cn } from '@/lib/utils';

import { useTableStore } from '../store';
import { Pagination } from './pagination';
import { SearchField } from './search-field';

export function GenericTable<DataType>({
  data,
  onClick,
  columns,
  children,
  searchable = false,
  searchPlaceholder,
  pinEnabled = false,
  name = 'table',
  tableRowClassName,
  emptyCellClassName,
  tableCellClassName,
  tableFilterClassName,
  initialState = {},
  searchColumns = [],
  onRowSelectionChange,
  sizeStyleProperty = 'width',
  emptyDescription = 'No results.',
  emptyTitle,
  emptyButton,
  isFiltered = false,
  disabledColumnsIdsToClick = [],
}: PropsWithChildren<{
  name?: string;
  data: DataType[];
  searchable?: boolean;
  pinEnabled?: boolean;
  columns: ColumnDef<DataType>[];
  emptyDescription?: string;
  emptyCellClassName?: string;
  searchColumns?: string[];
  tableRowClassName?: string;
  searchPlaceholder?: string;
  tableCellClassName?: string;
  tableFilterClassName?: string;
  onClick?: (item: DataType) => void;
  sizeStyleProperty?: 'width' | 'minWidth';
  initialState?: InitialTableState;
  onRowSelectionChange?: (selection: string[]) => void;
  emptyTitle?: string;
  emptyButton?: ReactNode;
  isFiltered?: boolean;
  disabledColumnsIdsToClick?: string[];
}>) {
  const testId = useGetTestId(name);
  const [rowSelection, setRowSelection] = useState({});
  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const { searchValue, setSearchValue } = useTableStore();

  const [debouncedRowSelection] = useDebounce(rowSelection, 300);

  const filteredData = useMemo(() => {
    const lowerTerm = searchValue.toLowerCase();
    if (!searchValue || !searchColumns.length) return data;

    return filter(data, (item) =>
      searchColumns.some((key) => {
        const val = get(item, key);
        return typeof val === 'string' && val.toLowerCase().includes(lowerTerm);
      })
    );
  }, [data, searchValue]);

  const table = useReactTable({
    data: filteredData,
    columns,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onRowSelectionChange: setRowSelection,
    getRowId: (originalRow) => (originalRow as { id: string }).id,
    state: {
      sorting,
      columnFilters,
      rowSelection,
    },
    defaultColumn: {
      size: Number.MAX_SAFE_INTEGER,
    },
    autoResetPageIndex: false,
    initialState: {
      pagination: {
        pageIndex: 0,
        pageSize: 15,
      },
      ...initialState,
    },
    columnResizeMode: 'onChange',
  });

  useEffect(() => {
    onRowSelectionChange?.(
      table.getSelectedRowModel().rows.map((row) => {
        const original = row.original as { id: string };

        return original.id;
      })
    );
  }, [Object.keys(debouncedRowSelection).length]);

  const getCommonPinningStyles = (column: Column<DataType>): CSSProperties => {
    if (!pinEnabled) return {};

    const isPinned = column.getIsPinned();
    const isLastLeftPinnedColumn =
      isPinned === 'left' && column.getIsLastColumn('left');
    const isFirstRightPinnedColumn =
      isPinned === 'right' && column.getIsFirstColumn('right');

    return {
      boxShadow: isLastLeftPinnedColumn
        ? '-2px 0 2px -2px gray inset'
        : isFirstRightPinnedColumn
          ? '2px 0 2px -2px gray inset'
          : undefined,
      left: isPinned === 'left' ? `${column.getStart('left')}px` : undefined,
      right: isPinned === 'right' ? `${column.getAfter('right')}px` : undefined,
      position: isPinned ? 'sticky' : 'relative',
      width: column.getSize(),
      zIndex: isPinned ? 1 : 0,
    };
  };

  return (
    <div className="w-full">
      <div
        className={cn(
         'flex justify-between items-center gap-4 mb-5',
          tableFilterClassName
        )}
      >
        {searchable && (
          <SearchField
            placeholder={searchPlaceholder}
            defaultValue={searchValue}
            className="w-full max-w-[400px]"
            onChange={(value) => setSearchValue(value)}
          />
        )}
        {children}
      </div>

      <Table>
        <TableHeader>
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id} className="bg-zinc-900 border-0">
              {headerGroup.headers.map((header) => {
                const size = header.getSize();

                return (
                  <TableHead
                    key={header.id}
                    style={{
                      [sizeStyleProperty]:
                        size === Number.MAX_SAFE_INTEGER
                          ? undefined
                          : `${header.getSize()}px`,
                      ...getCommonPinningStyles(header.column),
                    }}
                    className="bg-zinc-900"
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                  </TableHead>
                );
              })}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {table.getRowModel().rows?.length ? (
            table.getRowModel().rows.map((row, index) => (
              <TableRow
                key={`${row.id}-${index}`}
                data-testid={`${testId}-row-${index}`}
                className={cn(
                  Boolean(onClick) && 'cursor-pointer',
                  tableRowClassName
                )}
              >
                {row.getVisibleCells().map((cell) => {
                  return (
                    <TableCell
                      key={cell.id}
                      className={cn(
                        'capitalize bg-zinc-950',
                        cell.column.id === 'status' && 'p-0',
                        tableCellClassName
                      )}
                      style={{ ...getCommonPinningStyles(cell.column) }}
                      onClick={() =>
                        //disable click on some rows
                        disabledColumnsIdsToClick.includes(cell.column.id)
                          ? undefined
                          : onClick?.(row.original)
                      }
                    >
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </TableCell>
                  );
                })}
              </TableRow>
            ))
          ) : (
            <TableRow className={tableRowClassName} data-testid="empty-row">
              {searchValue || isFiltered ? (
                <TableCell
                  colSpan={columns.length}
                  className={cn(
                    'h-80 text-center text-zinc-500',
                    emptyCellClassName
                  )}
                >
                  No results found
                </TableCell>
              ) : (
                <TableCell
                  colSpan={columns.length}
                  className={cn('h-80', emptyCellClassName)}
                >
                  <div
                    className={cn(
                      'flex flex-col gap-2 justify-start items-start',
                      {
                        'items-center': !emptyTitle,
                      }
                    )}
                  >
                    <p className="text-md text-semibold">
                      {emptyTitle && emptyTitle}
                      <p
                        className={cn('text-zinc-500 text-xs', {
                          'text-md': !emptyTitle,
                        })}
                      >
                        {emptyDescription}
                      </p>
                    </p>
                    {emptyButton && emptyButton}
                  </div>
                </TableCell>
              )}
            </TableRow>
          )}
        </TableBody>
      </Table>

      <Pagination className="-ml-[24px] -mr-[24px]" table={table} />
    </div>
  );
}
